import { type EditableField, EditableFieldSchema } from "@ai-teach/utils";

export type {
  EditableAudio,
  EditableField,
  EditableImage,
  EditableOption,
  EditableValue,
} from "@ai-teach/utils";

export interface DraftSlide {
  id: string | null;
  sectionId: string;
  name: string;
  template: string;
  referenceSlideId: string;
  parentSlideName: string | null;
  error: string | null;
  deleted: boolean;
  fields: EditableField[];
}

interface RawDraftSlide {
  id?: string | null;
  sectionId: string;
  name: string;
  template: string;
  referenceSlideId: string;
  parentSlideName?: string | null;
  error?: string | null;
  deleted?: boolean | null;
  fields: { key: string; label: string; value: unknown }[];
}

export const CHECK_KINDS = ["wrongAnswer", "aiSounding", "offBook"] as const;
export type DraftCheckKind = (typeof CHECK_KINDS)[number];

export interface DraftCheck {
  slideId: string;
  fieldKey: string | null;
  kind: DraftCheckKind;
  note: string;
}

interface RawDraftCheck {
  slideId: string;
  fieldKey?: string | null;
  kind: string;
  note: string;
}

const isCheckKind = (kind: string): kind is DraftCheckKind =>
  (CHECK_KINDS as readonly string[]).includes(kind);

/** The checker's flags by slide, skipping the slides whose wording has since been edited. */
export function groupChecks(
  checks: readonly RawDraftCheck[],
  edited: ReadonlySet<string>
): Map<string, DraftCheck[]> {
  const bySlide = new Map<string, DraftCheck[]>();
  for (const check of checks) {
    if (edited.has(check.slideId) || !isCheckKind(check.kind)) continue;
    const found = bySlide.get(check.slideId) ?? [];
    found.push({
      slideId: check.slideId,
      fieldKey: check.fieldKey ?? null,
      kind: check.kind,
      note: check.note,
    });
    bySlide.set(check.slideId, found);
  }
  return bySlide;
}

export function parseDraft(slides: readonly RawDraftSlide[]): DraftSlide[] {
  return slides.map((slide) => ({
    id: slide.id ?? null,
    sectionId: slide.sectionId,
    name: slide.name,
    template: slide.template,
    referenceSlideId: slide.referenceSlideId,
    parentSlideName: slide.parentSlideName ?? null,
    error: slide.error ?? null,
    deleted: slide.deleted ?? false,
    fields: slide.fields.flatMap((field) => {
      const parsed = EditableFieldSchema.safeParse(field);
      return parsed.success ? [parsed.data] : [];
    }),
  }));
}

export function setDeleted(
  slides: DraftSlide[],
  slideIndex: number,
  deleted: boolean
): DraftSlide[] {
  return slides.map((slide, index) => (index === slideIndex ? { ...slide, deleted } : slide));
}

export function setSectionDeleted(
  slides: DraftSlide[],
  sectionId: string,
  deleted: boolean
): DraftSlide[] {
  return slides.map((slide) => (slide.sectionId === sectionId ? { ...slide, deleted } : slide));
}

export function renameSlide(slides: DraftSlide[], slideIndex: number, name: string): DraftSlide[] {
  return slides.map((slide, index) => (index === slideIndex ? { ...slide, name } : slide));
}

/** Takes a set at once: picking one phonics sound rewrites both the clip and the spelling. */
export function replaceFields(
  slides: DraftSlide[],
  slideIndex: number,
  fields: EditableField[]
): DraftSlide[] {
  const edited = new Map(fields.map((field) => [field.key, field] as const));
  return slides.map((slide, index) =>
    index === slideIndex
      ? { ...slide, fields: slide.fields.map((one) => edited.get(one.key) ?? one) }
      : slide
  );
}
